/**
 * A library reference in an undo command.
 *
 * `createSceneObjectFromData` builds a node from its data and stops there. A
 * reference is a node plus what its target derives, and data carries the node
 * alone. Rebuilt from data, a reference is an ordinary node that happens to hold
 * a URL: what its document derives is missing, and it is not a scene reference,
 * so `TraceScene.canParentUnder` has nothing against a child beneath it. A caller
 * that hands the derived nodes over as data gets them back as the scene's own,
 * and the next save writes them as files with a relative URI nothing resolves.
 *
 * So a command that rebuilds a subtree records, beside each node's data, what
 * data cannot carry, and puts it back after the rebuild. What that is depends
 * on what the reference resolved to when it was recorded:
 *
 * - a *document* (a library asset with a document of its own): its content is
 *   nodes beneath the reference. After the rebuild the nested load runs again
 *   (`TraceScene._hydrateReference`), which is what a document open does, for
 *   all the references at once. A reference that will not load does not stop the
 *   rest coming back; it is left as a document open leaves one, with nothing
 *   under it and still written as a link, and the command warns, naming it;
 * - a *file* (a bare GLB): the placement owns it, and its `Model` is in the
 *   node's data, so the rebuild loads the geometry by itself. What data drops is
 *   the runtime mark that says the node is a reference standing in for that file
 *   (`isSceneReference`, `_standInFileSrc`), so the marks are put back.
 *
 * Derived nodes are not the scene's, so a command does not record them. An
 * editor collects `[target, ...getDescendants()]`, which lists the derived nodes
 * too, and {@link isDerived} is how the command leaves them out: they come back
 * with the reference.
 */

import type { SceneObject } from '../../core/SceneObject';
import type { TraceScene } from '../../core/TraceScene';
import { Logger } from '../../utils/Logger';

/**
 * What a node's rebuild has to put back that its data does not carry. The
 * marks are runtime-only, so none of it is serialized into a scene; a command
 * records them in its own payload.
 */
export interface NodeMarks {
    /** A library reference (`isSceneReference`). */
    reference: boolean;
    /**
     * For a reference that resolved to one FILE: the file the placement owns
     * (`_standInFileSrc`). Absent for a reference to a document, whose content
     * is rebuilt by loading it.
     */
    standIn?: string;
    /** The original name of the file `src` points at (`files[k].name`), informational. */
    srcName?: string;
}

/** A node a command rebuilt, with what it recorded for it. */
export interface RebuiltNode {
    object: SceneObject;
    marks: NodeMarks | undefined;
}

/**
 * What `object`'s rebuild has to put back, or `undefined` when a rebuild from
 * data is all it needs (the common case: an ordinary node).
 */
export function readMarks(object: SceneObject): NodeMarks | undefined {
    const reference = object.isSceneReference;
    const standIn = object._standInFileSrc;
    const srcName = object.srcName;
    if (!reference && srcName === undefined) return undefined;
    return {
        reference,
        ...(reference && standIn !== undefined ? { standIn } : {}),
        ...(srcName !== undefined ? { srcName } : {}),
    };
}

/**
 * Whether a node a caller listed is derived content: `parent` is a reference, or
 * is itself in `derived`, the set of nodes already left out. The caller lists
 * parents before children, so one pass over its list is enough; it adds each
 * node this answers `true` for to the set.
 *
 * The parent is read while it is live, as a command reads everything at
 * construction, so this does not depend on the reference still standing when
 * the command is undone.
 */
export function isDerived(parent: SceneObject | null, derived: ReadonlySet<SceneObject>): boolean {
    return !!parent && (parent.isSceneReference || derived.has(parent));
}

/**
 * Put back what a rebuild from data left out, for every node the command just
 * rebuilt: the file's name, the marks of a reference to a file, and the nested
 * load of a reference to a document. The documents load together, one download
 * each, so the wait is the slowest piece and not the sum.
 *
 * Does not throw for a reference that will not load, or whose load throws: it
 * must not stop the rest of the scene coming back, and it is not silent either.
 * Every reference is marked before any of them is asked to load, so no piece
 * can keep the ones after it from being marked. A piece whose load fails is
 * left empty and named in one warning, and a save writes it as the link it is.
 */
export async function restoreReferences(
    scene: TraceScene,
    nodes: readonly RebuiltNode[],
    command: string,
    label: string,
): Promise<void> {
    const hydrating: Array<{ object: SceneObject; whole: Promise<boolean> }> = [];
    for (const { object, marks } of nodes) {
        if (!marks) continue;
        if (marks.srcName !== undefined) object._setSrcNameInternal(marks.srcName);
        if (!marks.reference) continue;
        // Marked before anything is asked to load: whatever the load does, this
        // is a reference.
        object._setSceneRefInternal(true);
        if (marks.standIn === undefined) {
            hydrating.push({ object, whole: loadAgain(scene, object, command, label) });
        } else {
            object._standInFileSrc = marks.standIn;
        }
    }
    const results = await Promise.all(hydrating.map(piece => piece.whole));
    const empty = hydrating.filter((_, i) => !results[i]).map(piece => piece.object);
    if (empty.length > 0) {
        Logger.warn(
            `[${command}] "${label}": ${empty.map(piece => `"${piece.name}" (${piece.src})`).join(', ')} `
            + `did not load again and ${empty.length === 1 ? 'stands' : 'stand'} empty`,
        );
    }
}

/**
 * The nested load of a reference, as an answer that is never an exception:
 * `true` when the reference is whole, `false` when it is not. `_hydrateReference`
 * already answers `false` for a load that fails. This is for one that throws or
 * rejects anyway (from the call itself, or from anything it awaits), which must
 * count as a load that failed, not as a failed undo.
 */
async function loadAgain(scene: TraceScene, object: SceneObject, command: string, label: string): Promise<boolean> {
    try {
        return await scene._hydrateReference(object);
    } catch (error) {
        Logger.error(`[${command}] "${label}": loading "${object.name}" (${object.src}) again threw:`, error);
        return false;
    }
}
